import {
  useEffect, useRef, useState, type ReactNode, type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Brain, Check, Copy, FileText, Info, Loader2, Play, Square, SquareCheck, X } from 'lucide-react';
import { TIP_BADGE } from './HoverTip';
import { PopMenu } from './PopMenu';
import { CALL_INSET, PromptPanel, usePromptCall, type TipPicture } from './PromptBadge';
import { type DrawnBox } from './BoxOverlay';
import { modalBackdropCls, modalCardCls, modalCloseCls, overlayCls } from './overlay';
import { AboveLayer, LAYER_STEP, useLayer } from './overlayLayer';
import { useLanguage } from '../../contexts/LanguageContext';
import { IS_LOCKED } from '../../apiBase';
import { clock, useWaited } from './waitClock';
import { useReportBusy } from './phaseBusy';

// EVERYTHING ABOUT ONE CALL, BEHIND ONE ICON: what was asked, and what came back.
//
// The two were two badges stacked beside the button, each opening its own panel on hover.
// Which is a poor way to READ either of them: a hover panel is held open by keeping the
// pointer inside it, so scrolling four hundred lines of prompt means steering rather than
// reading, and a stray pixel takes the whole thing away. Two of them side by side also made
// the pair look like two features rather than one call's two halves — and the second only
// appears once an answer exists, so the row changed shape under the pointer.
//
// So: one badge, a click, a menu of two, and a modal. A modal stays put. It closes on a click
// outside or on Escape, which is what everything else in this app that opens over the page
// does (see ConfirmDialog), and nothing closes it by accident.
//
// THE MENU IS A PORTAL, not an absolutely-positioned box under the badge. These sit inside
// tables that scroll sideways and inside panels with their own overflow, where an absolute
// box is clipped at the first ancestor that hides it — the same reason HoverTip portals its
// panel, and the same fix.

// One thing the menu offers: what it is called, the icon beside it, and the panel it opens.
// Exported with the menu itself, for a badge whose choices are not this file's two — see
// MasksBadge, where they are the boxes on a photograph and each one is its own call.
// `header`: what stays at the top of the window while the panel scrolls under it — the arrows
// that walk a badge's calls (see StepPromptNav).
export type Choice = { key: string; icon: ReactNode; label: string; panel: ReactNode; header?: ReactNode };

// MAKING THE CALL, from the badge that says what it asks — for a call that is one of several a
// button makes in a row and is worth making without the others. See DimensionsInfo: the size
// read is call 2 of Phase 2's four, and re-reading a drawing should not re-list the parts.
//
// The caller holds `busy`, because the caller is what the call's answer lands in.
export type CallRun = {
  // "Run" unless the call is better named by what it does.
  label?: string;
  onRun: () => void;
  busy?: boolean;
  disabled?: boolean;
  // WHAT TO RUN IT OVER, where there is a choice: pressing Run then opens a second list of these
  // instead of running — "All steps", "Step 1", … — and the pick is the run.
  // `sub` is a second line under the label, centred — what it covers, where that is long.
  // Without `many`, picking one runs it (`onRun`); with it, `onRun` is not asked.
  options?: { key: string; label: string; sub?: string; onRun?: () => void }[];
  // SEVERAL OPTIONS IN ONE RUN: each row ticks instead of running, `all` ticks or unticks every
  // one, and a last row — `run(n)` — runs the ticked keys, in the options' order whatever order
  // they were ticked in. It opens with nothing ticked, so a stray click starts nothing.
  many?: { all: string; run: (n: number) => string; onRun: (keys: string[]) => void };
  // HOW LONG, in seconds, where the RUN keeps the time — from a start it stores, so the clock
  // survives the badge remounting (leaving the phase, a refresh) and the last run's duration
  // is still there after it. Absent: the badge times the press itself, from when it saw it.
  elapsed?: number | null;
  // A CHOICE TO OPEN INSTEAD OF RUNNING: the key of the row whose window holds the real press.
  // The 3D generation badge's Run opens its pictures and settings (see MeshGenerateControls):
  // what it would spend depends on what is chosen there, so pressing it blind from a menu was
  // a run nobody had looked at. That row then leaves the list — Run IS it.
  opens?: string;
  // THE RUN THAT JUST ENDED WAS STOPPED: no "(took …)" after it. A stopped run took nothing — it
  // made nothing — and its time beside the button read as a finished run's.
  stopped?: boolean;
  // STOP, in the Run row's place while the run is busy — where given. No call is aborted (see
  // runHalt): the run stops sending new ones, and the one out lands and is kept.
  onStop?: () => void;
  // AND WHAT IT SAYS ONCE PRESSED, where stopping takes a while — a call waited for, a joint put
  // back: the row stays, spinning and not pressable, under these words.
  stopping?: string;
};

// One row of the menu, whatever it does. CENTRED IN ITS ROW, icon and words together: ranged
// left they sat against one edge of a panel sized by the other row, which reads as a list that
// has lost something rather than as things to choose between.
const ROW = 'flex w-full items-center justify-center gap-2 whitespace-nowrap px-4 py-2 text-xs transition-colors';

export function CallMenu({ anchor, choices, onPick, onClose, title, run }: {
  anchor: RefObject<HTMLElement | null>;
  choices: Choice[];
  onPick: (key: string) => void;
  onClose: () => void;
  // WHICH CALL THIS IS, in caps over the rows. A row of identical (i) badges says nothing
  // about which is which until one is opened, and the menu is the first thing that opens.
  title?: string;
  // And the call itself, above what it asks. See CallRun.
  run?: CallRun;
}) {
  const { t } = useLanguage();
  // The run's own list is showing in place of the rows, once Run is pressed on a run that has
  // options (see CallRun).
  const [picking, setPicking] = useState(false);
  const [ticked, setTicked] = useState<string[]>([]);
  const off = !run || run.disabled || run.busy || IS_LOCKED;
  // STOP UNDER THE RUN while a run that can be stopped is out, or is stopping (see CallRun).
  const stopRow = !!run && !picking && (!!run.stopping || (!!run.busy && !!run.onStop)) && !IS_LOCKED;
  const optionLabel = (o: NonNullable<CallRun['options']>[number]) => (o.sub ? (
    <span className="flex flex-col items-center">
      <span>{o.label}</span>
      <span className="text-[11px] font-normal text-slate-400">{o.sub}</span>
    </span>
  ) : o.label);
  const optionCls = `${ROW} font-medium tabular-nums text-slate-600 hover:bg-slate-100 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent`;
  const runCls = `${ROW} font-semibold tabular-nums text-blue-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent`;
  const tick = (on: boolean) => (on
    ? <SquareCheck className="h-3.5 w-3.5 text-blue-600" />
    : <Square className="h-3.5 w-3.5" />);

  return (
    <PopMenu anchor={anchor} onClose={onClose}>
      {title && (
        <p className="px-4 pb-1 pt-1.5 text-center text-[10px] font-bold uppercase tracking-wider text-slate-900">
          {title}
        </p>
      )}
      {run && picking && !run.many && run.options?.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => {
            onClose();
            o.onRun?.();
          }}
          disabled={off}
          className={optionCls}
        >
          <Play className="h-3.5 w-3.5" />
          {optionLabel(o)}
        </button>
      ))}
      {run?.many && picking && (() => {
        const { many } = run;
        const keys = (run.options ?? []).map((o) => o.key);
        const every = keys.length > 0 && keys.every((k) => ticked.includes(k));
        const chosen = keys.filter((k) => ticked.includes(k));
        return (
          <>
            <button type="button" onClick={() => setTicked(every ? [] : keys)} disabled={off} className={optionCls}>
              {tick(every)}
              {many.all}
            </button>
            {run.options?.map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => setTicked((t) => (t.includes(o.key) ? t.filter((k) => k !== o.key) : [...t, o.key]))}
                disabled={off}
                className={optionCls}
              >
                {tick(ticked.includes(o.key))}
                {optionLabel(o)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                onClose();
                many.onRun(chosen);
              }}
              disabled={off || !chosen.length}
              className={`${runCls} border-t border-slate-100`}
            >
              <Play className="h-3.5 w-3.5" />
              {many.run(chosen.length)}
            </button>
          </>
        );
      })()}
      {/* THE RUN'S OWN ROW STAYS WHILE IT IS OUT, spinning, with its clock — and Stop goes under
          it rather than in its place: what is running, for how long, and the way to end it. */}
      {run && !picking && (
        <button
          type="button"
          onClick={() => {
            if (run.opens) return onPick(run.opens);
            if (run.options?.length) return setPicking(true);
            onClose();
            run.onRun();
          }}
          // LOCKED HERE AND NOT BY THE PHASE'S FIELDSET. The menu is a portal on
          // document.body, outside the <fieldset disabled> that turns off every run button
          // in a locked build (DemoLock) — so this is the one run button that has to ask.
          // Not one that only OPENS a window: that spends nothing, and the window's own press
          // is where the lock and the rest of the reasons not to run are asked.
          disabled={!run.opens && off}
          className={runCls}
        >
          {run.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
          {run.label}
        </button>
      )}
      {stopRow && (
        <button
          type="button"
          onClick={() => {
            onClose();
            run.onStop?.();
          }}
          disabled={!!run.stopping}
          className={`${ROW} font-semibold text-rose-600 hover:bg-slate-100 disabled:cursor-default disabled:hover:bg-transparent`}
        >
          {run.stopping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Square className="h-3.5 w-3.5" />}
          {run.stopping ?? t('notice.cat.stop')}
        </button>
      )}
      {/* The row a Run OPENS leaves the list: Run is it, running or not. */}
      {!picking && choices.filter((c) => c.key !== run?.opens).map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={() => onPick(c.key)}
          className={`${ROW} font-medium text-slate-600 hover:bg-slate-100 hover:text-blue-600`}
        >
          {c.icon}
          {c.label}
        </button>
      ))}
    </PopMenu>
  );
}

// The reading window itself, in the card every dialog wears (see modalCardCls): white in
// light mode, slate-900 in dark. It was dark whatever the theme, navy on navy, on the grounds
// that a prompt and a raw reply are code more than prose — which is still true of the TEXT,
// so it keeps its monospace, on an inset of its own (CALL_INSET, see PromptPanel) that follows
// the theme too: a navy block on a white card was two colour schemes in one window, and one
// dialog in the app that ignored the theme read as another app.
// Exported because a call that does not fit the prompt-and-answer pair still has to be read
// in the same window: the second pass of the listing crops is one call PER BOX, so what it
// opens is a list of them rather than two panels (see MasksBadge).
export function CallModal({ title, copy, header, children, onClose }: {
  title: string;
  // FIXED OVER THE TEXT, outside the part that scrolls: the arrows that walk the badge's calls.
  // They were sticky inside it, and the frame's own top padding left a strip above them where
  // the scrolled-away lines kept showing through.
  header?: ReactNode;
  // What the copy button puts on the clipboard. Undefined: whatever the window says, read off
  // the DOM — which is right for the answer, whose sections are built by the caller and whose
  // text nobody holds as a string. The prompt passes its own, because what you want pasted is
  // the question and not the heading over it.
  //
  // AND `false` FOR NO BUTTON AT ALL. Copying is here for the things this window was built to
  // hold — four hundred lines of prompt, a model's reasoning, a raw reply, the things you put
  // in front of another model — and not every window that borrows it holds one of those. A
  // sentence ("This is a base part. No smaller components.") is read where it is: an icon
  // offering to put it on the clipboard is a control for a thing nobody is going to do, drawn
  // at the same weight as the one that closes the window. See MadePieceCard, both of whose
  // windows are a line or a diagram.
  copy?: string | false;
  children: ReactNode;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const body = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const z = useLayer() + LAYER_STEP;

  // COPYING IS THE WINDOW'S JOB, as it was the hover panel's (see HoverTip). What these hold
  // is a prompt, a model's reasoning, a raw reply — the things you put in front of another
  // model or in a message asking why it answered that — and selecting four hundred lines by
  // dragging is not a way to get them.
  const take = () => {
    const text = typeof copy === 'string' ? copy : body.current?.innerText ?? '';
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }).catch(() => {});
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Stopped here so it closes THIS and not whatever else is listening: a modal opened
      // over the mesh viewer would otherwise take the viewer away with it.
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return createPortal(
    <AboveLayer z={z}>
    {/* CENTRED IN THE CONTENT AREA, as every dialog is (overlayCls): it was centred in the
        window, which put it a hand's width left of the page it opened over — it holds the
        generators' pictures and settings now, answered and gone back from like any dialog. */}
    {/* THE SHARED BACKDROP, not the page's navy it used to be tinted with: every dialog dims
        the page the same way, so this one no longer announces itself as a different kind of
        thing. */}
    <div className={`${overlayCls} ${modalBackdropCls}`} style={{ zIndex: z }} onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className={`relative flex max-h-[85vh] w-[52rem] max-w-[94vw] flex-col ${modalCardCls}`}
      >
        {/* THE COPY BUTTON, just left of the close — which is the shared one (modalCloseCls),
            in the corner where every dialog keeps it. Same size, same grey, so the pair reads
            as the window's two controls rather than as a control and a stray icon. */}
        {copy !== false && (
          <button
            type="button"
            onClick={take}
            title={t(copied ? 'notice.tip.copied' : 'notice.tip.copy')}
            className="absolute right-11 top-3 z-10 rounded-lg p-1 text-slate-400 transition-colors hover:text-slate-700"
          >
            {copied
              ? <Check className="h-5 w-5 text-blue-600" />
              : <Copy className="h-5 w-5" />}
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label={t('notice.3d.turnClose')}
          className={modalCloseCls}
        >
          <X className="h-5 w-5" />
        </button>
        {/* SCROLLS INSIDE ITS OWN FRAME. The window keeps its size and the text moves in it,
            which is the whole reason this is a modal and not a hover panel: four hundred
            lines can be read at leisure, with the scrollbar where a scrollbar belongs. */}
        {/* The padding is what the text has to lose to be read, so it is the smallest a
            frame can wear and still look like one — the close and copy buttons sit in the
            top right of the frame and not in this. */}
        {/* CLEAR OF THE COPY AND CLOSE PAIR, which takes the top right corner to about 4.5rem in:
            at px-10 the arrows ran under the copy button. The same on the left, so the
            arrows stay centred on the window. */}
        {header && <div className="shrink-0 px-20 pt-3">{header}</div>}
        <div
          ref={body}
          className="overflow-y-auto overscroll-contain px-4 pb-4 pt-3 text-left text-slate-700"
        >
          {children}
        </div>
      </div>
    </div>
    </AboveLayer>,
    document.body,
  );
}

export function CallBadge({
  promptLabel, load, more, boxesFor, onToggleBox, onTogglePicture, revision, answer, promptNav,
  noPrompt = false, extra, title, run, named = false, picker, unavailable,
}: {
  // WHY THIS CALL CANNOT BE MADE, when it cannot (Louis 2026-11-03): the badge stays in the row,
  // greyed, so the phase still shows its whole sequence, and a click says why instead of
  // opening a menu with nothing behind it.
  unavailable?: string;
  // The prompt's pictures as a picker — see PromptPanel.
  picker?: ReactNode;
  // What the question is called — the modal's heading, and the title over the prompt.
  promptLabel: string;
  load: () => Promise<{ prompt: string; urls?: (string | TipPicture)[]; model?: string }>;
  more?: () => Promise<TipPicture[]>;
  boxesFor?: (url: string) => DrawnBox[] | undefined;
  onToggleBox?: (url: string, index: number) => void;
  // A whole picture in or out of the call — the caption under it becomes the switch. See
  // PromptPanel, where the argument for putting it on the name is written.
  onTogglePicture?: (url: string) => void;
  revision?: string;
  // AND WHAT CAME BACK, when anything has: the reasoning over the raw reply, built by whoever
  // holds the answer (see reasoningTooltip, which is what every one of these passes).
  //
  // Absent until the call has been made, and the menu is then a menu of one — so the badge
  // opens the prompt directly rather than offering a choice with nothing to choose.
  answer?: { label: string; content: ReactNode };
  // A control belonging to the PROMPT panel, drawn above it — for a badge whose call is made
  // many times over and whose question is a different question each time. It moves `revision`,
  // which is what refetches. See StepPromptNav, the one thing that uses it.
  promptNav?: ReactNode;
  // THIS BADGE'S OWN PROMPT ROW IS NOT ITS STORY. Two callers say so for two reasons. One
  // has no question at all: a component's size is computed here, out of boxes and
  // photographs, and nothing is sent anywhere — offering "Prompt" beside its answer would be
  // offering to show a question nobody put (see EstimateInfo). The other has better ones —
  // the written engine's passes each carry the question that was actually put, and the plain
  // row would describe a call that is none of them (see WrittenCallBadge).
  //
  // Only takes effect once there IS something else to read, an answer or an extra, since a
  // badge with none of it is a badge with nothing behind it. Before the arithmetic has run,
  // the one entry is what it WOULD do; after, it is what it did. Never two.
  noPrompt?: boolean;
  // ANYTHING THIS CALL HAS THAT IS NOT ITS PROMPT OR ITS ANSWER. One caller has a middle:
  // the written engine makes its model twice, and the second call's question — the renders
  // of the first attempt — is neither the prompt somebody would press the button with nor
  // the answer that came back. Listed after both, because it is the middle of the story.
  extra?: Choice[];
  // The call's name, in caps over the menu — see CallMenu.
  title?: string;
  // AND A WAY TO MAKE IT FROM HERE. The badge spins while the call is out.
  run?: CallRun;
  // ITS NAME ON THE BADGE, beside the icon: a pill rather than a circle. For a row of badges
  // — Phase 1's, which replaced its buttons, and Phase 2's calls under its button — where
  // identical circles say which is which only once one is opened. The name is `title`, the menu's own
  // heading, so the two cannot disagree. The icon after the name: the name is read first.
  named?: boolean;
}) {
  const { t } = useLanguage();
  const call = usePromptCall({ load, more, revision });
  // HOW LONG THE RUN HAS BEEN GOING, on its row — and once it is done, how long it took. Held
  // here and not in the menu, which is mounted only while open: a run started, the menu closed
  // and opened again a minute later still has to know when it began. Not persisted: a run from
  // before the page was opened is not one this badge watched.
  const running = !!run?.busy;
  useReportBusy(running);
  const waited = useWaited(running);
  const ran = useRef(false);
  if (running) ran.current = true;
  const runLabel = run?.label ?? t('notice.tip.menuRun');
  const seconds = run?.elapsed ?? waited;
  const timed = running || (!run?.stopped && (ran.current || run?.elapsed != null));
  const timedRun = run && {
    ...run,
    label: !timed
      ? runLabel
      : running
        ? `${runLabel} (${clock(seconds)})`
        : `${runLabel} ${t('notice.steps.tookTime').replace('{t}', clock(seconds))}`,
  };
  const badge = useRef<HTMLButtonElement>(null);
  // WHETHER the menu is open, and which panel is open, or null. Two states rather than one
  // enum: picking from the menu closes it and opens the modal, and a single value would have
  // to describe both halves of that moment.
  //
  // A boolean and not the badge's rectangle, which is what it held while the menu was placed
  // once: where the badge is is a question with a different answer after every scroll, and
  // the menu asks it of the ref itself now.
  const [open, setOpen] = useState(false);
  const [showing, setShowing] = useState<string | null>(null);

  // THE QUESTION AND WHAT CAME BACK ON ONE ROW — "Prompt, reasoning & output" — on every
  // badge in the app: one panel, the prompt first and the answer under it once there is one.
  // Unless the prompt row is dropped, when the answer needs a row of its own — it has no
  // prompt panel left to sit under.
  const dropPrompt = noPrompt && !!(answer || extra?.length);
  const choices: Choice[] = [
    ...(dropPrompt ? [] : [{
      key: 'prompt',
      icon: <FileText className="h-3.5 w-3.5" />,
      label: t('notice.tip.menuPromptAll'),
      header: promptNav,
      panel: (
        <>
          <PromptPanel
            label={promptLabel}
            call={call}
            boxesFor={boxesFor}
            onToggleBox={onToggleBox}
            onTogglePicture={onTogglePicture}
            picker={picker}
          />
          {answer && (
            <div className="mt-6 border-t border-slate-200 pt-4">
              <p className="mb-3 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {answer.label}
              </p>
              <div className={CALL_INSET}>{answer.content}</div>
            </div>
          )}
        </>
      ),
    }]),
    ...(answer && dropPrompt ? [{
      key: 'answer',
      icon: <Brain className="h-3.5 w-3.5" />,
      label: t('notice.tip.menuAnswer'),
      panel: <div className={CALL_INSET}>{answer.content}</div>,
    }] : []),
    ...(extra || []),
  ];

  const chosen = choices.find((c) => c.key === showing);

  // THE WINDOW THE PRESS IS IN CLOSES WHEN ITS RUN ENDS (see CallRun.opens): the settings a 3D
  // generation was started from stood over the model it had just made, and had to be closed by hand
  // to see it. Only that window, and only on a run this badge watched end.
  const wasRunning = useRef(running);
  useEffect(() => {
    if (wasRunning.current && !running && run?.opens && showing === run.opens) setShowing(null);
    wasRunning.current = running;
  }, [running, run?.opens, showing]);

  // THE FETCH STARTS WITH THE MENU, not with the choice. Somebody who has opened the menu is
  // about to read one of these, and the prompt is the one that costs a round trip: asking now
  // means the modal opens on its words rather than on "Building the prompt…".
  const press = () => {
    if (open) return setOpen(false);
    if (unavailable) return setOpen(true);
    // AND ONLY WHERE A ROW WILL SHOW IT. `load` is a round trip to build words nobody is
    // going to read once noPrompt has dropped that row.
    if (choices.some((c) => c.key === 'prompt')) call.open();
    // A MENU WHENEVER THERE IS MORE TO IT THAN ONE PANEL: two choices, a Run row, or a title.
    // A titled badge sits in a row of titled badges, and one that skipped its menu because it
    // had no answer yet opened a modal where its neighbours open a menu — the same icon doing
    // two different things depending on something nobody can see from outside.
    const menu = choices.length > 1 || !!run || !!title;
    setOpen(menu);
    // WHICHEVER THE ONE IS, and it was written as 'prompt'. True while a badge with a single
    // choice could only be a badge with no answer yet; false the moment one of them dropped
    // its prompt instead (see noPrompt), where the key named a choice that was no longer in
    // the list — so `chosen` came back undefined and the modal opened on nothing at all.
    if (!menu && choices.length === 1) setShowing(choices[0].key);
  };

  return (
    <>
      <button
        ref={badge}
        type="button"
        onClick={press}
        title={promptLabel}
        aria-label={promptLabel}
        className={`${named && title ? `${TIP_BADGE} gap-1.5 pl-2.5` : TIP_BADGE}${unavailable ? ' opacity-40' : ''}`}
      >
        {named && title && (
          <span className="text-[10px] font-bold uppercase tracking-wider">{title}</span>
        )}
        {run?.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Info className="h-3.5 w-3.5" />}
      </button>

      {open && unavailable && (
        <PopMenu anchor={badge} onClose={() => setOpen(false)}>
          {title && (
            <p className="px-4 pb-1 pt-1.5 text-center text-[10px] font-bold uppercase tracking-wider text-slate-900">
              {title}
            </p>
          )}
          <p className="max-w-[18rem] px-4 py-2 text-center text-xs text-slate-600">{unavailable}</p>
        </PopMenu>
      )}

      {open && !unavailable && (
        <CallMenu
          anchor={badge}
          title={title}
          run={timedRun}
          choices={choices}
          onClose={() => setOpen(false)}
          onPick={(key) => {
            setOpen(false);
            setShowing(key);
          }}
        />
      )}

      {chosen && (
        <CallModal
          title={chosen.key === 'answer' && answer ? answer.label
            : chosen.key === 'prompt' ? promptLabel : chosen.label}
          copy={chosen.key === 'prompt' ? call.prompt : undefined}
          header={chosen.header}
          onClose={() => setShowing(null)}
        >
          {/* The prompt panel draws its own heading — the label, the model and the word
              count — so the modal adds none over it. The answer's sections carry their own
              headings too (see tipSection); what it has not got is a name for the whole,
              which is what this line is. */}
          {chosen.key === 'answer' && answer && (
            <p className="mb-3 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {answer.label}
            </p>
          )}
          {chosen.panel}
        </CallModal>
      )}
    </>
  );
}
